import { useEffect, useRef, useState } from 'react';
import { inject, observer } from 'mobx-react';
import { querySelector, throttle, toggleGlobalScroll } from '@/utils';
import dynamic from 'next/dynamic';
import useDebounce from 'ahooks/lib/useDebounce';
import { GlobalTracker } from '@/utils/Tracker';
import ComponentsErrorBoundary from '@/components/ErrorBoundary/ComponentsErrorBoundary';
import styles from './index.module.less';
import { ArrowLeft, ArrowRight } from '@/components/Icons/Arrow';
import { useWidthTransition } from '../../../utils/hooks/useWidthTransition';
import Cart from './components/Cart';
import Personal from './components/Personal';
import Search from '@/components/Header/Nav/components/Search';
import { useSearchPage } from '@/pages/search/utils';
import { isTextTypeNav } from '@/utils/mobxUtils/nav';
import { isEmpty } from 'lodash';

const SubMenu = dynamic(import('@/components/Header/Nav/components/SubMenu'), { ssr: false });

let recordAccessoryOpen = false;

const Nav = inject(
  'area',
  'app',
  'meta'
)(
  observer((props) => {
    const [curNav, setCurNav] = useState(null);
    const [visible, setVisible] = useState(false);
    const [eleHeader, setEleHeader] = useState(null);
    const { setStateValue, navStateInfos, navCurStyle, isNavTextTransparent } = props.meta;
    const {
      country: { code },
      LANG,
    } = props.area;
    const { G_IsHomePage, isMobile, isOrderConfirmPage, isCartPage } = props.app;
    const { showNavContent } = props;

    // 加防抖解决切换菜单太快的问题
    const debouncedVisible = useDebounce(visible, { wait: 150 });
    const debouncedCurNav = useDebounce(curNav, { wait: 150 });

    useEffect(() => {
      // IIFE模式下使用async
      const init = async () => {
        let eleHeader = await querySelector('#g-header');
        setEleHeader(eleHeader);
      };

      init();

      const navEle = document.querySelector('#g-nav');
      setTimeout(() => {
        navEle.style.transition = '0.2s all ease';
      }, 100);
    }, []);

    // 控制导航栏样式的鼠标悬浮事件
    const handleSetNavIsHover = (type) => {
      if (!type) return;
      if (type === 'notHover') {
        if (navStateInfos.isHover) {
          navStateInfos.isHover = false;
          setStateValue('navStateInfos', navStateInfos);
        }
      } else {
        if (!navStateInfos.isHover) {
          navStateInfos.isHover = true;
          setStateValue('navStateInfos', navStateInfos);
        }
      }
    };

    // 控制导航是否可见，但是没有下拉（主题config变动）
    const toggleNavBarVisible = (visible) => {
      handleSetNavIsHover(visible ? 'hover' : 'notHover');
      setVisible(visible);
    };

    // 鼠标hover整个NavBar，内部处理透明文字的hover事件，鼠标挪动到箭头和活动文字上要背景反白
    const hoverNavBar = (visible) => {
      isNavTextTransparent && toggleNavBarVisible(visible);
      // 移出时清空当前的curNav，避免SubMenu被渲染，导致底部有一条白色的线
      // 移出到SubMenu的情况，SubMenu会重新设置一次CurNav，保证SubMemu显示正常（使用deBounceVisible以后一个SubMenu的配置为准）
      !visible && toggleCurNavItem(null);
    };

    // hover 导航NavItem，展示底部的Submemu，处理展开的curNav
    const toggleCurNavItem = (curNav) => {
      setCurNav(curNav);

      if (!isEmpty(curNav)) {
        if (curNav.type === 'accessoryNew') {
          GlobalTracker.Common({ isMobile, action: 'open_accessory_tab', category: 'Accessory_tab' });
          recordAccessoryOpen = true;
        }

        if (curNav.type !== 'accessoryNew' && recordAccessoryOpen) {
          GlobalTracker.Common({ isMobile, action: 'close_accessory_tab', category: 'Accessory_tab' });
          recordAccessoryOpen = false;
        }
      }
    };

    // hoverNavItem 处理非透明文字导航栏背景
    const hoverNavItem = (isHover, curNav) => {
      isHover &&
        curNav?.categoryInsideName &&
        GlobalTracker.navigation({ action: 'menu_item_hover', eventLabel: curNav.categoryInsideName });
      toggleCurNavItem(curNav);

      // 如果没hover / 活动 / 文字链菜单 则鼠标移动上去背景不显示
      if (!isHover || isTextTypeNav(curNav)) {
        // 如果当前的nav状态透明，那不会马上处理NavBar的反白，而是等NavBar处理（因为活动文字链和左右箭头不在navItem里）
        if (!isNavTextTransparent) return toggleNavBarVisible(false);
        return;
      }
      toggleNavBarVisible(true);
    };

    // hoverSubMenu 鼠标移入 / 移出SubMenu，重新设置curNav
    const hoverSubMenuContent = (isHover, curNav) => {
      toggleCurNavItem(curNav);
      toggleNavBarVisible(isHover);
    };

    // 用于固定导航在全局禁止滚动的情况下的抖动
    const handleScrollBar = async () => {
      if (!eleHeader) return;
      toggleGlobalScroll(visible);
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => handleScrollBar(), [debouncedVisible]);

    // 是否为搜索页面
    const { isSearchResultsPage } = useSearchPage();

    // nav的底部border，搜索结果页无需border-bottom
    const navBottomBorder = isSearchResultsPage ? 'none' : `1px solid ${navCurStyle?.navBottomBorder ?? '#efefef'}`;

    return (
      <div
        id='g-nav'
        className={styles.nav}
        style={{
          backgroundColor: navCurStyle?.backGroundColor ?? '#fff',
          borderBottom: navBottomBorder,
        }}
      >
        <div className={styles.wrap} data-is-home-page={G_IsHomePage || isCartPage} id='navWrap'>
          <div className={styles.l}>
            <a
              className={styles.logo}
              href='/'
              onClick={() => {
                GlobalTracker.navigation({ action: 'back_to_homepage' });
              }}
              data-c={code}
              style={{
                backgroundImage: `url(${code === 'CN' ? navCurStyle?.storeLogo.cn : navCurStyle?.storeLogo.en})`,
              }}
            />
            <ComponentsErrorBoundary name='G_Header Nav NavList'>
              {showNavContent && <NavList curNav={curNav} hoverNavBar={hoverNavBar} hoverNavItem={hoverNavItem} />}
            </ComponentsErrorBoundary>
          </div>
          {/* 仅在订单确认页展示安全图标 */}
          {isOrderConfirmPage && (
            <div className={styles.r}>
              <img
                src='https://res.insta360.com/static/eeb4a2b9f8ef8038355cc8bb7892f696/Frame.png'
                alt=''
                width={24}
                height={24}
              />
              <span className={styles.secure}>{LANG['common.label.securePay']}</span>
            </div>
          )}
          {showNavContent && (
            <div className={styles.r}>
              <Search />
              <Cart />
              <Personal />
            </div>
          )}
        </div>
        {debouncedVisible && debouncedCurNav && !isTextTypeNav(debouncedCurNav) && (
          <SubMenu curNav={debouncedCurNav} onToggleVisible={hoverSubMenuContent} />
        )}
      </div>
    );
  })
);

/**
 * @param hoverNavItem 回调，鼠标移入 / 移出item项，需要指定当前的curNav内容，显示下拉的SubMenu
 * @param hoverNavBar 回调，鼠标移入 / 移出NavBar范围，处理NavBar的样式
 * @param toggleNavBarVisible 单纯处理NavBar的背景反白，不管subMenu
 */
const NavList = inject('meta')(
  observer((props) => {
    const {
      meta: { navigationBarInfos, navCurStyle },
      hoverNavItem,
      hoverNavBar,
    } = props;
    const navListRef = useRef(null);

    // 透明文字，基本只在活动时使用。
    const fontTransparent = navCurStyle?.fontColor === 'transparent';

    const onClick = (nav) => {
      GlobalTracker.navigation({ action: 'menu_item_click', eventLabel: nav.categoryInsideName });
      hoverNavItem(false);
    };

    const { isOverflow, allowLeftScroll, allowRightScroll, scrollToLeft, scrollToRight, reCalcReachedEdge } =
      useWidthTransition({
        dom: navListRef.current,
      });

    const clickRightArrow = () => {
      scrollToRight(navListRef.current.clientWidth / 4);
    };

    const clickLeftArrow = () => {
      scrollToLeft(navListRef.current.clientWidth / 4);
    };

    useEffect(() => {
      // 监听页面视窗的缩放，如果缩小需要实时更新滑动

      const resizeCb = throttle(() => {
        reCalcReachedEdge();
      }, 200);

      window.addEventListener('resize', resizeCb);

      return () => {
        window.removeEventListener('resize', resizeCb);
      };
    }, []);

    return (
      <div
        className={styles.navListWrap}
        data-navstate={navCurStyle.type}
        onMouseEnter={() => hoverNavBar(true)}
        onMouseLeave={() => hoverNavBar(false)}
      >
        <ul className={styles.list} ref={navListRef} onMouseLeave={() => hoverNavItem(false)}>
          {navigationBarInfos.map((nav) => {
            const { isCampaignGroupNav, isCampaignTextNav, formatHref = {} } = nav;
            const showRedInfo = ['campaign'].includes(nav?.type) && !fontTransparent;
            const isLink = ['textLink'].includes(nav?.type) || isCampaignTextNav;
            return (
              <h2 key={nav.id}>
                <li
                  className={styles.item}
                  data-iscampaign={showRedInfo}
                  data-islink={isLink}
                  onClick={() => onClick(nav)}
                  onMouseEnter={() => hoverNavItem(true, nav)}
                >
                  {isCampaignGroupNav && (
                    <span style={{ color: navCurStyle?.fontColor ?? '#262629' }}>{nav.categoryName}</span>
                  )}
                  {!isCampaignGroupNav && (
                    <a
                      href={`${formatHref?.href}?from=nav`}
                      style={{
                        color: navCurStyle?.fontColor ?? '#262629',
                        stroke: navCurStyle?.fontColor ?? '#262629',
                      }}
                      target={formatHref?.target}
                    >
                      {nav?.extraInfo?.icon && nav?.extraInfo?.icon}
                      {nav.categoryName}
                    </a>
                  )}
                </li>
              </h2>
            );
          })}
        </ul>
        {isOverflow && (
          <div className={styles.arrow} data-transparent={fontTransparent}>
            <div className={styles.arrowLeft} data-click={allowLeftScroll} onClick={clickLeftArrow}>
              <ArrowLeft />
            </div>
            <div className={styles.arrowRight} data-click={allowRightScroll} onClick={clickRightArrow}>
              <ArrowRight />
            </div>
          </div>
        )}
      </div>
    );
  })
);

export default Nav;
